import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, backendUrl } from "@/lib/api";
import { Loader2, Search, Heart, MessageSquare, Eye, Phone, UserCircle2 } from "lucide-react";
import { errorDetail } from "@/lib/utils";

const EVENT_ICON = {
  view: Eye,
  like: Heart,
  unlike: Heart,
  chat: MessageSquare,
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
function buildWhatsappUrl(detail) {
  const c = detail.customer;
  const liked = [];
  // Traverse newest → oldest and collect the actual products liked (with names).
  const seen = new Set();
  for (const s of (detail.sessions || [])) {
    for (const ev of (s.events || [])) {
      if (ev.type === "like" && ev.product_id && !seen.has(ev.product_id)) {
        seen.add(ev.product_id);
        liked.push({ name: ev.product_name || "Product", code: ev.product_code });
      }
    }
  }
  const lines = [
    `Hi ${c.name}, thank you for visiting Heda Plywoods today.`,
    "",
    "Here's the wishlist from your visit:",
    ...liked.map((p, i) => `${i + 1}. ${p.name}${p.code ? ` (Code: ${p.code})` : ""}`),
    "",
    "Reply here anytime for quotes, availability or samples.",
    "— Team Heda",
  ];
  const msg = encodeURIComponent(lines.join("\n"));
  // wa.me expects country code prefixed; assume India (+91) if not present.
  const mob = c.mobile.startsWith("+") ? c.mobile : `91${c.mobile}`;
  return `https://wa.me/${mob}?text=${msg}`;
}

  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [wishlistBusy, setWishlistBusy] = useState(false);

  const generateWishlistPdf = async (mobile) => {
    setWishlistBusy(true);
    try {
      const { data } = await api.post(`/showroom/customers/${mobile}/wishlist-pdf`);
      // Open the WhatsApp deep-link (with the PDF URL pre-populated) in a new tab.
      window.open(data.whatsapp_link, "_blank", "noopener");
      // Also open the PDF itself so staff can quickly preview before sending.
      window.open(backendUrl(data.pdf_url), "_blank", "noopener");
    } catch (e) {
      alert(errorDetail(e) || "Could not generate wishlist PDF");
    } finally {
      setWishlistBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/showroom/customers");
        if (!cancelled) setCustomers(data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/showroom/customers/${selected.mobile}`);
        if (!cancelled) setDetail(data);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected]);

  const filtered = (customers || []).filter((c) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (c.name || "").toLowerCase().includes(q) || (c.mobile || "").includes(q);
  });

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="hed-overline">Showroom</div>
        <h1 className="mt-1 font-display text-2xl font-bold text-heda-ink md:text-[30px]">
          Customers &amp; their journeys.
        </h1>
        <p className="mt-1 max-w-2xl text-[13px] text-heda-sub">
          Every customer who&rsquo;s used the showroom tablet. Click to see what they liked,
          asked and viewed — across every visit.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-[300px_1fr]">
        {/* List */}
        <div className="heda-card p-3">
          <div className="relative mb-3">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-heda-mute" />
            <input
              data-testid="customer-search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search name or mobile"
              className="heda-input py-2.5 pl-9"
            />
          </div>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 size={20} className="animate-spin text-heda-gold" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-2 py-8 text-center text-[13px] text-heda-mute">No customers yet. Start a session to see them here.</div>
          ) : (
            <ul className="space-y-1.5">
              {filtered.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => setSelected(c)}
                    data-testid={`customer-row-${c.mobile}`}
                    className={`w-full rounded-xl border px-3 py-2.5 text-left transition ${
                      selected?.id === c.id
                        ? "border-heda-gold bg-heda-gold-tint"
                        : "border-heda-line bg-white hover:border-heda-gold"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <UserCircle2 size={14} className="flex-shrink-0 text-heda-gold" />
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-heda-ink">{c.name}</span>
                      {c.visit_count > 1 && (
                        <span className="rounded-full bg-heda-gold-tint px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-heda-gold">
                          ×{c.visit_count}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1 text-[11.5px] text-heda-mute">
                      <Phone size={9} /> {c.mobile}
                      <span className="ml-auto">{fmtDate(c.last_seen_at).split(",")[0]}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="heda-card min-h-[420px] min-w-0 p-5">
          {!selected ? (
            <div className="flex h-full min-h-[380px] flex-col items-center justify-center text-center">
              <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-heda-olive-tint">
                <UserCircle2 size={26} className="text-heda-olive" />
              </div>
              <p className="text-sm text-heda-mute">Pick a customer to see their full journey.</p>
            </div>
          ) : detailLoading ? (
            <div className="inline-flex items-center gap-2 text-sm text-heda-mute">
              <Loader2 size={16} className="animate-spin text-heda-gold" /> Loading history…
            </div>
          ) : !detail ? null : (
            <div>
              <div className="mb-5 flex flex-wrap items-center gap-3 border-b border-heda-line pb-4">
                <div className="min-w-0">
                  <div className="font-display text-[20px] font-bold text-heda-ink">{detail.customer.name}</div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11.5px] text-heda-mute">
                    <span className="inline-flex items-center gap-1"><Phone size={11} /> {detail.customer.mobile}</span>
                    <span>First seen {fmtDate(detail.customer.first_seen_at)}</span>
                  </div>
                </div>
                <div className="inline-flex flex-wrap items-center gap-2 text-xs sm:ml-auto">
                  <span className="rounded-full bg-heda-gold-tint px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-heda-gold">
                    {detail.customer.visit_count} visits
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-heda-gold-tint px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-heda-gold">
                    <Heart size={10} /> {(detail.customer.liked_product_ids || []).length} liked
                  </span>
                  {(detail.customer.liked_product_ids || []).length > 0 && (
                    <>
                      <a
                        href={buildWhatsappUrl(detail)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="whatsapp-wishlist-button"
                        className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1.5 text-[12.5px] font-medium text-white transition hover:bg-emerald-700"
                      >
                        <MessageSquare size={12} /> Send wishlist to WhatsApp
                      </a>
                      <button
                        onClick={() => generateWishlistPdf(detail.customer.mobile)}
                        disabled={wishlistBusy}
                        data-testid="wishlist-pdf-button"
                        className="heda-pill-ink disabled:opacity-40"
                      >
                        {wishlistBusy && <Loader2 size={12} className="animate-spin" />}
                        {wishlistBusy ? "Generating…" : "AI Curated Quote PDF → WhatsApp"}
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-5">
                {(detail.sessions || []).map((s) => (
                  <div key={s.id} className="border-l-2 border-heda-gold/40 pl-4">
                    <div className="hed-overline">
                      Visit · {fmtDate(s.started_at)}
                    </div>
                    <ul className="mt-2 space-y-1.5">
                      {(s.events || []).map((ev, idx) => {
                        const Icon = EVENT_ICON[ev.type] || Eye;
                        const verb = {
                          view: "viewed",
                          like: "liked",
                          unlike: "unliked",
                          chat: "asked about",
                        }[ev.type] || ev.type;
                        return (
                          <li
                            key={idx}
                            className="flex items-start gap-2 text-sm"
                            data-testid={`event-${ev.type}`}
                          >
                            <Icon
                              size={12}
                              className={`mt-1 flex-shrink-0 ${
                                ev.type === "like"
                                  ? "fill-red-700 text-red-700"
                                  : "text-heda-gold"
                              }`}
                            />
                            <span className="min-w-0 flex-1 break-words">
                              <span className="text-heda-mute">{verb}</span>{" "}
                              <span className="font-medium text-heda-ink">
                                {ev.product_name || ev.product_code || "—"}
                              </span>
                              {ev.detail && (
                                <span className="text-heda-sub">: {ev.detail}</span>
                              )}
                            </span>
                            <span className="whitespace-nowrap text-[10.5px] tabular-nums text-heda-mute">
                              {new Date(ev.ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </li>
                        );
                      })}
                      {(s.events || []).length === 0 && (
                        <li className="text-xs text-heda-mute">No activity recorded.</li>
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
